import { JUMP_TYPES, pjasUriToJumpPath } from '@/func/entityLinks';
import { pushAndWait } from './navigate';
import type { AutopilotFunctionFactory } from './types';

/**
 * Exposes the canonical `pjas://<type>:<info>` internal-link scheme to the autopilot so it can
 * navigate to a specific entity (e.g. `pjas://order:22`). Resolution is delegated to the in-app
 * jumper (`/jump/...`), so any type the jumper knows is reachable.
 */
export const createOpenLinkFunction: AutopilotFunctionFactory = ({ router }) => ({
  name: 'open_link',
  description:
    'Navigate to an EXISTING internal app resource by its pjas:// link. Examples: "pjas://order:15" ' +
    'opens order 15, "pjas://company:8" opens company 8. Only use a link you actually see as the ' +
    '`link` field on an item in a list or form context — never fabricate one or invent ids like ' +
    '"new". This opens things; it does NOT create them — to create an entity use the matching ' +
    'create/goto_create_* function on its list page. Supported types: ' +
    JUMP_TYPES.join(', ') +
    '.',
  parameters: {
    type: 'object',
    properties: {
      uri: {
        type: 'string',
        description: 'A pjas:// internal link, e.g. "pjas://order:22".',
      },
    },
    required: ['uri'],
  },
  handler: async (rawArgs) => {
    const args = (rawArgs ?? {}) as { uri?: string };
    const path = args.uri ? pjasUriToJumpPath(args.uri) : null;
    if (!path) {
      return { success: false, error: `Invalid or unsupported link: ${args.uri ?? '(none)'}` };
    }
    await pushAndWait(router, path);
    return { success: true, uri: args.uri };
  },
});
